import { FC, memo } from 'react';

import {
  DownOutlined,
  UpOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  QuestionOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Popover, Tooltip } from 'antd';

import { ReportMenu } from '../../../../shared/common/components/nav/components/reportMenu/reportMenu';
import UniversalReportTitle from '../../../../shared/common/components/reports/dashboards/title/UniversalReportTitle';
import { BlockVisible } from '../../types';

import styles from './DashboardToolbar.module.scss';

interface DashboardToolbarProps {
  /** Название дашборда */
  dashboardName?: string;
  /** Текущий дашборд для меню */
  currentDashboard: any;
  /** Масштаб */
  scale: number;
  /** Увеличить масштаб */
  onZoomIn: () => void;
  /** Уменьшить масштаб */
  onZoomOut: () => void;
  /** Сбросить масштаб */
  onResetZoom: () => void;
  /** Настройки свёрнуты */
  isSettingsCollapsed: boolean;
  /** Переключить сворачивание настроек */
  onToggleSettings: () => void;
  /** Полноэкранный режим */
  isFullscreen: boolean;
  /** Переключить полноэкранный режим */
  onToggleFullscreen: () => void;
  /** Фиксированный заголовок */
  isFixedHeader: boolean;
  /** Открыть модалку добавления блока */
  setAddBlockVisible: React.Dispatch<BlockVisible>;
  /** Открыть справку */
  onOpenHelp: () => void;
  /** Компонент параметров (мемоизированный) */
  paramsComponent: React.ReactNode;
  /** Callback для обновления данных дашборда (без перезагрузки страницы) */
  onRefresh?: () => void;
  /** Функция сохранения дашборда (из useSaveDashboardShortcut) */
  onSave?: () => void;
  /** Открыть модалку "Сохранить как" (управляется из Dashboards) */
  saveAsModalVisible?: boolean;
  /** Закрыть модалку "Сохранить как" */
  onCloseSaveAsModal?: () => void;
}

/**
 * Панель инструментов дашборда.
 * Содержит:
 * - Название и меню дашборда
 * - Управление масштабом
 * - Сворачивание настроек
 * - Полноэкранный режим
 * - Кнопки действий
 */
const DashboardToolbar: FC<DashboardToolbarProps> = ({
  dashboardName,
  currentDashboard,
  scale,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  isSettingsCollapsed,
  onToggleSettings,
  isFullscreen,
  onToggleFullscreen,
  isFixedHeader,
  setAddBlockVisible,
  onOpenHelp,
  paramsComponent,
  onRefresh,
  onSave,
  saveAsModalVisible,
  onCloseSaveAsModal,
}) => {
  return (
    <div
      className={styles.toolbar}
      style={{
        borderRadius: isFullscreen ? '0' : '15px',
        padding: isFullscreen ? '8px 16px' : undefined,
        paddingBottom: isSettingsCollapsed ? '0' : '12px',
        backgroundColor: isSettingsCollapsed ? 'transparent' : '#fff',
      }}
    >
      {/* Компактная панель управления */}
      <div
        className={styles.controlPanel}
        style={{
          marginBottom: isSettingsCollapsed ? '0' : '8px',
          background: isSettingsCollapsed ? '#fff' : 'transparent',
          borderRadius: isSettingsCollapsed ? '15px' : '0',
          border: isSettingsCollapsed ? '1px solid #f0f0f0' : 'none',
        }}
      >
        {/* Левая часть */}
        <div className={styles.leftSection}>
          {isSettingsCollapsed ? (
            // В свёрнутом режиме показываем период
            <div>{paramsComponent}</div>
          ) : (
            // В развёрнутом режиме показываем название и помощь
            <>
              <div className={styles.dashboardName}>
                {dashboardName || 'Новый дашборд'}
              </div>
              {currentDashboard?.id && (
                <ReportMenu isDashboard={true} item={currentDashboard} trigger="click" />
              )}
              {!isFullscreen && (
                <Tooltip title="Как работать с дашбордом?">
                  <button onClick={onOpenHelp} className={styles.helpButton}>
                    <QuestionOutlined className={styles.helpIcon} />
                    <span>Помощь</span>
                  </button>
                </Tooltip>
              )}
            </>
          )}
        </div>

        {/* Правая часть: кнопки управления */}
        <div className={styles.rightSection}>
          {/* Горячие клавиши */}
          <Popover
            trigger="click"
            placement="bottomRight"
            showArrow={false}
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 340 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Сохранить дашборд</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>S</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Отменить действие</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Z</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Повторить действие</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Shift</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Z</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Выделить все блоки</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>A</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Масштаб</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>колёсико</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Полноэкранный режим</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Shift</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>F</kbd></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Снять выделение / Выход</span>
                  <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Esc</kbd>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, padding: '6px 10px', background: '#fafafa', borderRadius: '6px' }}>
                  <span style={{ fontSize: 13 }}>Сброс: очистка кеша, локальных хранилищ и перезагрузка</span>
                  <span style={{ whiteSpace: 'nowrap' }}><kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Ctrl</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>Shift</kbd> + <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '11px' }}>K</kbd></span>
                </div>
                <div style={{ fontSize: '11px', color: '#8c8c8c', padding: '2px 10px' }}>
                  На macOS вместо Ctrl используйте Cmd (⌘)
                </div>
              </div>
            }
          >
            <button
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#8c8c8c',
                fontSize: 13,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <ThunderboltOutlined style={{ fontSize: 12 }} />
              <span>Горячие клавиши</span>
            </button>
          </Popover>

          {/* Масштаб */}
          <div className={styles.zoomControls}>
            <Tooltip title="Уменьшить (Ctrl + колёсико)">
              <Button
                data-help-id="dashboard-zoom-out"
                type="text"
                size="small"
                icon={<ZoomOutOutlined className={styles.zoomIcon} />}
                onClick={onZoomOut}
                className={styles.zoomButton}
              />
            </Tooltip>
            <span
              data-help-id="dashboard-zoom-reset"
              className={styles.zoomValue}
              onClick={onResetZoom}
              title="Сбросить масштаб"
            >
              {Math.round(scale * 100)}%
            </span>
            <Tooltip title="Увеличить (Ctrl + колёсико)">
              <Button
                data-help-id="dashboard-zoom-in"
                type="text"
                size="small"
                icon={<ZoomInOutlined className={styles.zoomIcon} />}
                onClick={onZoomIn}
                className={styles.zoomButton}
              />
            </Tooltip>
          </div>

          {/* Свернуть/развернуть настройки */}
          <Tooltip title={isSettingsCollapsed ? 'Показать настройки' : 'Скрыть настройки'}>
            <Button
              data-help-id="dashboard-settings"
              type="text"
              icon={
                isSettingsCollapsed ? (
                  <DownOutlined style={{ fontSize: '11px' }} />
                ) : (
                  <UpOutlined style={{ fontSize: '11px' }} />
                )
              }
              onClick={onToggleSettings}
              className={styles.controlButton}
            />
          </Tooltip>

          {/* Полноэкранный режим */}
          <Tooltip title={isFullscreen ? 'Выйти (ESC)' : 'На весь экран'}>
            <Button
              data-help-id="dashboard-fullscreen"
              type="text"
              icon={
                isFullscreen ? (
                  <FullscreenExitOutlined style={{ fontSize: '15px' }} />
                ) : (
                  <FullscreenOutlined style={{ fontSize: '15px' }} />
                )
              }
              onClick={onToggleFullscreen}
              className={styles.controlButton}
            />
          </Tooltip>

          {/* Разделитель */}
          <div className={styles.divider} />

          {/* Кнопки действий */}
          {!isFixedHeader && (
            <UniversalReportTitle
              setAddBlockVisible={setAddBlockVisible}
              isCompact={true}
              onRefresh={onRefresh}
              onSave={onSave}
              saveAsModalVisible={saveAsModalVisible}
              onCloseSaveAsModal={onCloseSaveAsModal}
            />
          )}
        </div>
      </div>

      {/* Настройки (показываются только в развёрнутом режиме) */}
      {!isSettingsCollapsed && (
        <div className={styles.settingsPanel}>
          {paramsComponent}
        </div>
      )}
    </div>
  );
};

export default memo(DashboardToolbar);
